import React, { useState } from 'react';
import {
  Alert as AlertComponent,
  CssOverrideType,
  COLORS,
  Flex,
  Box,
} from '@dripcapital/dripui-v1';
import { SeverityStatus } from 'types';
import { DAJ_COLORS, FONT } from 'utils';
import { SeverityTypeEnum } from 'enums';
import Close from '@mui/icons-material/Close';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Error from '@mui/icons-material/Error';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

type Props = CssOverrideType & {
  severity: SeverityStatus;
  variant?: SeverityTypeEnum;
  title?: string;
  children?: React.ReactNode;
  onClose?: (event: React.SyntheticEvent) => void;
  close?: boolean;
};

const Alert: React.FC<Props> = ({
  severity,
  variant,
  title = '',
  children,
  onClose,
  close = false,
  _sx = {},
  className = '',
}: Props) => {
  const [hideAlert, setHideAlert] = useState<boolean>(false);

  const updateStyles = (severity: string) => {
    switch (severity) {
      case 'success':
        return (
          <CheckCircle fontSize="inherit" sx={{ fill: DAJ_COLORS.GREEN }} />
        );
      case 'error':
        return (
          <Close
            fontSize="inherit"
            sx={{
              fill: DAJ_COLORS.ORANGE_500,
              border: `2px solid ${DAJ_COLORS.ORANGE_500}`,
              borderRadius: '50px',
            }}
          />
        );
      case 'warning':
        return <Error fontSize="inherit" sx={{ fill: COLORS.ORANGE }} />;
      case 'info':
        return <InfoOutlined sx={{ fill: DAJ_COLORS.SKY_BLUE_500 }} />;
    }
  };

  if (hideAlert) {
    return null;
  }

  return (
    <AlertComponent
      dataTestId="alert-element"
      severity={severity}
      variant={variant}
      title={
        <Flex alignItems={'center'}>
          {updateStyles(severity)}
          <Box _sx={{ width: '8px' }}></Box>
          {title}
        </Flex>
      }
      className={className}
      _sx={{
        ..._sx,
        '.MuiAlertTitle-root': {
          display: !title ? 'none !important' : 'initial',
          marginBottom: children ? '0.35em' : 0,
        },
        '.MuiAlert-icon': {
          display: 'none',
        },
        '.MuiAlert-message': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
        },
        padding: '8px 16px',
      }}
      _alertTitleSx={{
        '.MuiTypography-h4': {
          fontWeight: FONT.WEIGHT.BOLD,
          fontSize: FONT.SIZES.F16,
          color: DAJ_COLORS.NOBLE_BLUE_500,
        },
      }}
      onClose={!close ? onClose : () => setHideAlert(true)}
    >
      {children ? <Box _sx={{ marginLeft: 3 }}>{children}</Box> : ''}
    </AlertComponent>
  );
};

export default Alert;
